<template>
    <VfModal id="audio-selection" @click.stop>
        <template #header>Speaker/Microphone Selection</template>

        <label>Speakers:</label>
        <div class="row">
            <VfSmartSelect v-model="selectedSpeakerDevice" :options="speakerDevices" :formatter="d => d.label" />
            <button
                v-tooltip="isPlayingTestSound ? 'Stop playing test sound' : 'Play test sound'"
                type="button"
                class="zs-btn zs-btn--icon"
                @click="toggleTestSound"
            >
                <i class="fa" :class="isPlayingTestSound ? 'fa-stop' : 'fa-play'" />
            </button>
        </div>

        <label>Microphone:</label>
        <VfSmartSelect v-model="selectedMicrophoneDevice" :options="microphoneDevices" :formatter="d => d.label" />
        <AVMedia class="meter" :media="inputMediaStream" type="vbar" vbar-bg-color="white" :canv-width="336" />

        <audio ref="audioEl" src="/assets/ring.mp3" loop preload="auto" @play="isPlayingTestSound = true" @pause="isPlayingTestSound = false" />

        <template #footer>
            <button type="button" class="zs-btn" @click.stop="close">Close</button>
        </template>
    </VfModal>
</template>

<script lang="ts" setup>
import { VfModal, VfSmartSelect, handleErrorAndAlert } from '@zyno-io/vue-foundation';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { AVMedia } from 'vue-audio-visual';

import { softphoneMgr } from '@/modules/talk/shared/softphone.manager';
import { playMedia } from '@/shared/helpers/media.helpers';
import { usePersistedPreference } from '@/shared/helpers/prefs.helper';

const props = defineProps<{ callback: () => void }>();

const audioEl = ref<HTMLAudioElement>();

const devices = ref<MediaDeviceInfo[]>([]);
const selectedSpeakerDevice = ref<MediaDeviceInfo | null>(null);
const selectedMicrophoneDevice = ref<MediaDeviceInfo | null>(null);
const inputMediaStream = ref<MediaStream>();
const isPlayingTestSound = ref(false);

const speakerDevices = computed(() => devices.value.filter(d => d.kind === 'audiooutput'));
const microphoneDevices = computed(() => devices.value.filter(d => d.kind === 'audioinput'));

const speakerDeviceId = usePersistedPreference<string>('talk', 'softphoneSpeakerDeviceId');
const microphoneDeviceId = usePersistedPreference<string>('talk', 'softphoneMicrophoneDeviceId');

onMounted(async () => {
    devices.value = await navigator.mediaDevices.enumerateDevices();

    if (speakerDeviceId.value) {
        selectedSpeakerDevice.value = speakerDevices.value.find(d => d.deviceId === speakerDeviceId.value) ?? null;
    }
    if (!selectedSpeakerDevice.value) {
        selectedSpeakerDevice.value = speakerDevices.value.find(d => d.deviceId === 'default') ?? null;
    }

    const configuredInputDevice: MediaDeviceInfo | undefined = microphoneDeviceId.value
        ? devices.value.find(d => d.deviceId === microphoneDeviceId.value)
        : undefined;
    inputMediaStream.value = await navigator.mediaDevices.getUserMedia({
        audio: configuredInputDevice ? { deviceId: { exact: configuredInputDevice.deviceId } } : true
    });

    const inputTrack = inputMediaStream.value.getAudioTracks()[0];
    const inputDeviceId = inputTrack?.getSettings().deviceId;
    selectedMicrophoneDevice.value = devices.value.find(d => d.deviceId === inputDeviceId) ?? null;

    watch(selectedMicrophoneDevice, updateMediaStream);
});

onUnmounted(() => {
    inputMediaStream.value?.getTracks().forEach(track => track.stop());
});

async function updateMediaStream() {
    inputMediaStream.value?.getTracks().forEach(track => track.stop());
    inputMediaStream.value = undefined;
    if (!selectedMicrophoneDevice.value) return;

    inputMediaStream.value = await navigator.mediaDevices.getUserMedia({
        audio: {
            deviceId: { exact: selectedMicrophoneDevice.value.deviceId }
        }
    });
}

async function toggleTestSound() {
    if (!selectedSpeakerDevice.value) return;
    if (isPlayingTestSound.value) {
        audioEl.value!.pause();
    } else {
        await audioEl.value!.setSinkId(selectedSpeakerDevice.value.deviceId);
        playMedia(audioEl.value!).catch(err => handleErrorAndAlert(err, { title: 'Failed to play test sound' }));
    }
}

function close() {
    if (isPlayingTestSound.value) {
        audioEl.value!.pause();
    }
    props.callback();
}

watch(selectedSpeakerDevice, async () => {
    if (selectedSpeakerDevice.value) {
        if (isPlayingTestSound.value) {
            audioEl.value!.setSinkId(selectedSpeakerDevice.value.deviceId);
        }
        speakerDeviceId.value = selectedSpeakerDevice.value.deviceId;
        // Update active calls immediately
        await softphoneMgr.setSpeakerDevice(selectedSpeakerDevice.value.deviceId);
    }
});

watch(selectedMicrophoneDevice, async () => {
    if (selectedMicrophoneDevice.value) {
        microphoneDeviceId.value = selectedMicrophoneDevice.value.deviceId;
        // Update active calls immediately
        await softphoneMgr.setMicrophoneDevice(selectedMicrophoneDevice.value.deviceId);
    }
});
</script>

<style scoped>
@reference '#tw.css';

label {
    @apply block mb-1 font-semibold w-96;

    &:not(:first-child) {
        @apply mt-4;
    }
}

.row {
    @apply flex gap-1;

    :first-child {
        @apply flex-1;
    }
}

.meter {
    @apply w-full h-3 mt-1;
}
</style>
